import { uuid } from '../../../../CodesThatsGottaGoSomewhereElse';
import { Composition } from '../../../../concepts/revive/Composition';
import { Store, StoreState } from '../../../../concepts/revive/Store';
import { LocalStorageService, StorageService } from '../../../../platform/StorageService';
import { resolve } from '../../../Container';
import {
    ButtonStripWidget,
    ButtonWidget,
    ColorSelectorWidget,
    DivideWidget,
    EmojiSelectorWidget,
    InlineWidget, MenuWidget,
    PopoutWidget, PopperWidgetProps, SelectorWidgetProps, SliderWidget,
    TextInputWidget,
    Widget,
    WidgetBaseProps,
    WidgetContainer,
    WidgetContainerPosition,
    WidgetContainerProps,
    WidgetContainerStyle
} from './WidgetInterface';

const baseState = {
    active: false,
    enabled: true,
    visible: true,
    name: null as string | null,
    tag: null as string | null,
    width: null as number | null,
    height: null as number | null,
    weight: null as number | null,
};

const baseButtonLikeState = {
    text: null as string | null,
    image: null as string | null,
};

const baseActionState = {
    onAction() { }
};

const basePopperState = {
    open: false,
    onOpen() { },
    onClose() { },
};

const baseSelectorState = {
    ...basePopperState,
    value: null as any,
    onSelect() { },
};

const initialButtonState = { ...baseState, ...baseButtonLikeState, ...baseActionState };

const initialButtonStripState = {
    ...baseState,
    ...baseButtonLikeState,
    ...basePopperState,
    items: [] as readonly ButtonWidget[],
    layout: 'row' as string,
};

const initialColorSelectorState = {
    ...baseState,
    ...baseButtonLikeState,
    ...baseSelectorState,
    allowNull: false,
    autoSelect: false,
    groups: false,
};

const initialDivideState = { ...baseState }

const initialEmojiSelectorState = { ...baseState, ...baseButtonLikeState, ...baseSelectorState };

const initialInlineState = { ...baseState, ui: null as any };

const initialMenuState = {
    ...baseState,
    ...baseButtonLikeState,
    ...basePopperState,
    items: [] as readonly ButtonWidget[],
    itemsLayout: 'column',
    itemsWidth: null as number | null,
    padding: 1,
};

const initialPopoutState = {
    ...baseState,
    ...baseButtonLikeState,
    ...baseSelectorState,
    autoClose: true,
    ui: null as any,
    placement: null as string,
};

const initialSliderState = {
    ...baseState,
    ...baseButtonLikeState,
    ...baseSelectorState,
    min: 0,
    max: 100,
    value: 0,
};

const initialTextInputState = { ...baseState, ...baseButtonLikeState, ...baseSelectorState, value: '' };

const autoInvokeSelectOnValueChange: WidgetConfigurator<Widget<WidgetBaseProps & SelectorWidgetProps<any>>> = (widget) =>
    widget.observe((next, prev) => {
        if (next.value !== prev.value) {
            widget.onSelect();
        }
    });

const autoInvokeOpenOrCloseOnOpenChange: WidgetConfigurator<Widget<WidgetBaseProps & PopperWidgetProps>> = (widget) =>
    widget.observe((next, prev) => {
        if (next.open !== prev.open) {
            if (next.open) widget.onOpen();
            if (prev.open) widget.onClose();
        }
    });

type WidgetConfigurator<T extends Widget> = (widget: T) => void;

const createFactory = <T extends Widget>(type: string, templateState: StoreState<T>, ...configurators: WidgetConfigurator<T>[]) =>
    (initialState?: Partial<StoreState<T>>): T => {
        const widget = Store.create(Object.assign({}, templateState, initialState), { id: uuid(), type });
        configurators.forEach(x => x(widget));
        return widget;
    };

export class WidgetManager extends Composition<WidgetContainer> {

    @resolve(LocalStorageService) private localData!: StorageService;

    public createContainer = (initialState?: Partial<WidgetContainerProps>) => Object.assign(new WidgetContainerImpl(), initialState ?? {});

    public createButton = createFactory<ButtonWidget>(
        'button', initialButtonState);

    public createButtonStrip = createFactory<ButtonStripWidget>(
        'buttonStrip', initialButtonStripState, autoInvokeOpenOrCloseOnOpenChange);

    public createColorSelector = createFactory<ColorSelectorWidget>(
        'colorSelector',
        initialColorSelectorState,
        // w => w.update({ value: w.value ?? this.localData.get('deqo/ui/lastColor') }),
        autoInvokeSelectOnValueChange,
        autoInvokeOpenOrCloseOnOpenChange,
    );

    public createDivide = createFactory<DivideWidget>(
        'divide', initialDivideState);

    public createEmojiSelector = createFactory<EmojiSelectorWidget>(
        'emojiSelector', initialEmojiSelectorState, autoInvokeSelectOnValueChange, autoInvokeOpenOrCloseOnOpenChange);

    public createInline = createFactory<InlineWidget>(
        'inline', initialInlineState);

    public createMenu = createFactory<MenuWidget>(
        'menu', initialMenuState, autoInvokeOpenOrCloseOnOpenChange);

    public createPopout = createFactory<PopoutWidget>(
        'popout', initialPopoutState, autoInvokeSelectOnValueChange, autoInvokeOpenOrCloseOnOpenChange);

    public createSlider = createFactory<SliderWidget>(
        'slider', initialSliderState, autoInvokeSelectOnValueChange, autoInvokeOpenOrCloseOnOpenChange);

    public createTextInput = createFactory<TextInputWidget>(
        'textInput', initialTextInputState, autoInvokeSelectOnValueChange, autoInvokeOpenOrCloseOnOpenChange);

    public get(name: string) {
        return this.find(x => x.name === name) ?? null;
    }

    public has(name: string) {
        return !!this.get(name);
    }

    public delete(name: string) {
        const entry = this.find(x => x.name === name) ?? null;
        if (entry) this.remove(entry);
        return !!entry;
    }
}

class WidgetContainerImpl extends Composition<Widget> implements WidgetContainer {

    public readonly id: string = uuid();
    public name: string = '';
    public tag: string = '';
    public position: WidgetContainerPosition = ['docked', 'bottomLeft'];
    public direction: 'column' | 'row' = 'row';
    public style: WidgetContainerStyle = 'float';
    // public padding: string | null = null;
    public scale: number = 1;
    public weight: number | null = null;
    public data: Record<string, unknown> = {};

    public get<T extends Widget<any>>(name: string): T | null {
        return this.find(x => x.name === name) ?? null;
    }
}
